/*
 * Copyright (c) 2020 General Electric Company. All rights reserved.
 *
 * The copyright to the computer software herein is the property of
 * General Electric Company. The software may be used and/or copied only
 * with the written permission of General Electric Company or in accordance
 * with the terms and conditions stipulated in the agreement/contract
 * under which the software has been supplied.
 */

import * as _ from 'underscore';
import { Injectable } from '@angular/core';
import { Logger } from '@core/lib/services';
import { ProxyDataService } from '@app/core/http';
import { appConstants } from '@app/common/app-constants';
import { MessageService } from '@core/lib/services/message.service';
import { AppConfigService } from '@app/common/app-config.service';

const log = new Logger('P2pNotificationService');

@Injectable({
  providedIn: 'root'
})
export class P2pNotificationService {
  private autoAckTimer;

  constructor(
    private dataService: ProxyDataService,
    private messageService: MessageService,
    private appConfigService: AppConfigService
  ) {}

  notifyP2P(user, subject, payload): Promise<any> {
    return new Promise((resolve, reject) => {
      payload.name = subject;
      payload.action = 'request';

      this.dataService.httpPost(appConstants.appURLs.notification, payload).then(
        () => {
          resolve();
        },
        errorObj => {
          log.error('Error occurred while broadcasting p2p notification ' + JSON.stringify(errorObj));
          reject();
        }
      );
    });
  }

  notifyP2PViaServer(user, subject, url, payload, timeoutValue, AutoAckTimeoutValue): Promise<any> {
    timeoutValue = timeoutValue || 40000;
    let timer;
    let subscription;

    return new Promise((resolve, reject) => {
      subscription = this.messageService.on(subject + '.response').subscribe(data => {
        if (data.receiver !== this.appConfigService.getUserId()) {
          return;
        }
        resolve(data);
        subscription.unsubscribe();
        if (!!timer) {
          clearTimeout(timer);
        }
        if (!!this.autoAckTimer) {
          clearTimeout(this.autoAckTimer);
        }
      });

      payload.name = subject;
      payload.action = 'request';

      this.dataService.create(url, payload).then(appConstants.noop, errorObj => {
        log.error('Error occurred while broadcasting p2p notification ' + JSON.stringify(errorObj));
        reject();
      });

      // Start the timer
      timer = setTimeout(() => {
        log.warn('Did not get a response from the user within' + parseInt(timeoutValue) / 1000 + 's');
        reject();
        if (!!subscription) {
          subscription.unsubscribe();
        }
      }, timeoutValue);

      // Start the timer auto ack
      this.autoAckTimer = setTimeout(() => {
        if (!!timer) {
          clearTimeout(timer);
        }
        log.warn(
          'Did not get a response from the user within in Auto Ack time ' + parseInt(AutoAckTimeoutValue) / 1000 + 's'
        );
        reject();
        if (!!subscription) {
          subscription.unsubscribe();
        }
      }, AutoAckTimeoutValue);
    });
  }

  onMessage(subject, callback) {
    return this.messageService.on(subject + '.' + 'request').subscribe(target => {
      let found = false;
      if (_.isArray(target.receiver)) {
        found = _.find(target.receiver, user => {
          return user === this.appConfigService.getUserId();
        });
      }

      if (target.receiver === this.appConfigService.getUserId() || found || target.receiver === '*') {
        callback(target);
      }
    });
  }

  /**
   * This method is used to respond to the notify request for specific subject/topic with the payload
   * For ex:- If you want to respond to the takecontrol(subject/topic) request message with some payload,
   * then use it as below
   * p2pNotificationService.respond('takecontrol',somepayload);
   */
  respond(subject, url, payload) {
    payload.action = 'response';
    if (payload.status === 'autoAck') {
      payload.action = 'lock-release-auto-response';
    }
    payload.name = subject;
    if (payload.status === 'error') {
      this.dataService.httpPost(appConstants.appURLs.notification, payload).then(appConstants.noop, errorObj => {
        log.error('Error occurred while broadcasting p2p notification ' + JSON.stringify(errorObj));
      });
    } else {
      this.dataService.create(url, payload).then(appConstants.noop, errorObj => {
        log.error('Error occurred while broadcasting p2p notification ' + JSON.stringify(errorObj));
      });
    }
  }

  notifyToCancelAutoAckTimer() {
    if (!!this.autoAckTimer) {
      clearTimeout(this.autoAckTimer);
    }
  }
}
